import React, {ReactNode, useRef} from 'react';
import {defaultPopperOptions, IWithPopper, Popper} from '~/components/Popper';
import {classByIntent, Intent} from '~/lib/intent';
import {useKeyPressCallback} from '~/lib/keyPress';
import {useOutsideClick} from '~/lib/outsideClick';
import style from './index.module.scss';

interface IDropDownProps extends IWithPopper {
    children: ReactNode;
    childrenClassName?: string;
    content: (props: { toClose: () => void }) => ReactNode;
    disabled?: boolean;
    isOpen: boolean;
    onToggle: (isOpen: boolean) => void;
    rightIcon?: ReactNode;
    className?: string;
    intent?: Intent;
    tabIndex?: number;
}

export const DropDown = ({
    isOpen,
    disabled,
    usePopper,
    dropUp,
    children,
    childrenClassName,
    rightIcon,
    popperOptions = defaultPopperOptions,
    onToggle,
    content,
    intent,
    className,
    tabIndex
}: IDropDownProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const childrenRef = useRef<HTMLDivElement>(null);

    useOutsideClick(contentRef, () => onToggle(false));
    useKeyPressCallback('Escape', () => onToggle(false));

    const classes = [style.dropDown, className];
    if (isOpen) {
        classes.push(style.open);
    }
    if (usePopper) {
        classes.push(style.usePopper);
    }
    if (dropUp) {
        classes.push(style.dropUp);
    }
    if (disabled) {
        classes.push(style.disabled);
    }
    const renderContent = () => usePopper ?
        <Popper {...popperOptions} parentRef={childrenRef}
            children={content({toClose: () => onToggle(false)})} onOutsideClick={() => onToggle(false)}/> :
        <div className={style.content}>
            <div className={style.contentInner}>
                <div className={style.contentInnerBody}>{content({toClose: () => onToggle(false)})}</div>
            </div>
        </div>;

    return <div tabIndex={tabIndex ?? (disabled ? undefined : 0)} className={classes.join(' ')} ref={contentRef}>
        <div ref={childrenRef} onClick={() => {
            if (!disabled) {
                onToggle(!isOpen);
            }
        }} className={[style.children, childrenClassName, classByIntent(intent)].join(' ')}>
            <div className={style.childrenInner}>{children}</div>
            <span className={style.childrenArrow}>{rightIcon || <i className={'fa fa-chevron-down'}/>}</span>
        </div>
        {isOpen && renderContent()}
    </div>;
};
